import { Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { environment } from 'environments/environment';
import { AuthenticationService } from 'app/auth/service';
import { AuthSyncService } from 'app/services/auth/auth-sync.service';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { isErpSessionReadyForAppFeatures } from 'app/services/auth/erp-auth-token.util';
import { AppShellLayoutService } from 'app/services/layout/app-shell-layout.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _authSyncService: AuthSyncService,
    private _logicielCaisseAuth: LogicielCaisseAuthService,
    private _appShellLayout: AppShellLayoutService
  ) {}

  private getHubUrl(): string {
    return ((environment as { hubUrl?: string }).hubUrl || 'https://front-hub.agrosilo-solutions.com').replace(/\/$/, '');
  }

  private redirectToHubLogin(returnUrl: string): void {
    const isHub = (environment.currentModule || '') === 'hub';
    if (isHub) {
      void this._router.navigate(['/login'], { queryParams: { returnUrl } });
      return;
    }
    const q = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
    window.location.href = `${this.getHubUrl()}/login${q}`;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const pathOnly = (state.url || '').split('?')[0];
    if ((environment.currentModule || '') === 'hub' && pathOnly.startsWith('/dashboards')) {
      void this._router.navigateByUrl('/home');
      return false;
    }

    const synced = this._authSyncService.syncFromQueryParams();
    if (synced && isErpSessionReadyForAppFeatures()) {
      this._logicielCaisseAuth.autoConnectAfterErpLogin();
    }
    if (state.url.startsWith('/home')) {
      const goto = this._authSyncService.consumeGoto();
      if (goto) {
        this._router.navigateByUrl(goto);
        return false;
      }
    }

    const user = localStorage.getItem('currentUser');
    let currentUser: { is_active?: boolean; stations?: unknown[]; role?: string } | null = null;

    if (user) {
      try {
        currentUser = JSON.parse(user);
      } catch {
        localStorage.removeItem('currentUser');
      }
    }

    if (currentUser) {
      if (currentUser.is_active === false && state.url !== '/token') {
        this._router.navigate(['/token']);
        return false;
      } else if (
        (!currentUser.stations || currentUser.stations.length == 0) &&
        state.url !== '/create-station' &&
        state.url !== '/token'
      ) {
        this._router.navigate(['/create-station']);
        return false;
      }

      if (route.data.roles && route.data.roles.indexOf(currentUser.role) === -1) {
        this._router.navigate(['/pages/miscellaneous/not-authorized']);
        return false;
      }

      const path = (state.url || '').split('?')[0];
      if (path.startsWith('/main') || path.startsWith('/home')) {
        this._appShellLayout.restoreAfterAuthPages();
      }

      return true;
    }

    this.redirectToHubLogin(state.url);
    return false;
  }
}
